import { useLaunchDarklyFlag } from '@/hooks/flags/useLaunchDarklyFlag';
import { useBnplEligibility } from '@/hooks/payments/useBnplEligibility';
import type { Shop } from '@/types/appointment';
import type { Cart } from '@/types/booking';
import { BNPL_EXPERIMENT_FLAG_KEY } from '@/utils/bnplExperiment';
import {
  BNPL_EXPERIMENT_VARIANTS,
  type BnplExperimentVariant,
} from '@/utils/tracking/ldEvents';

import type { UseBnplExperimentVariantResult } from './types';

export { BNPL_EXPERIMENT_FLAG_KEY };

/**
 * A variation renamed or mistyped in the LaunchDarkly dashboard must degrade to
 * the control arm rather than leak an unexpected string into event payloads.
 */
const toVariant = (value: unknown): BnplExperimentVariant =>
  BNPL_EXPERIMENT_VARIANTS.find((variant): boolean => variant === value) ??
  'control';

/**
 * Resolves the BNPL consideration experiment arm for a checkout.
 *
 * Enrollment requires BNPL eligibility only — any provider the payment
 * session reports (SQR-15886 widened this from "Afterpay eligible" so
 * Klarna-only shops enter the experiment) — and the flag is read once it
 * holds. Ineligible checkouts (non-US shops, sub-minimum carts, shops with
 * every provider disabled) convert at a structurally zero rate, so enrolling
 * them inflates the denominator and blows out the experiment's runtime
 * estimate.
 *
 * Identification state is deliberately not a condition: how a guest is keyed
 * is LaunchDarkly's concern (see `identify-guest-contexts`), and gating on it
 * here silently reverted every guest to control while that flag was off.
 */
export const useBnplExperimentVariant = (
  cart: Cart | null | undefined,
  shop: Shop | null | undefined,
): UseBnplExperimentVariantResult => {
  const { isEligible: isEnrolled, eligibleMethods } = useBnplEligibility(
    cart,
    shop,
  );

  const flagValue = useLaunchDarklyFlag<unknown>(
    BNPL_EXPERIMENT_FLAG_KEY,
    'control',
    { enabled: isEnrolled },
  );

  return {
    variant: isEnrolled ? toVariant(flagValue) : 'control',
    isEnrolled,
    eligibleMethods,
  };
};
